import { useCallback, useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { getCards } from 'api/Cards';

import { ia, iaRa } from 'lib/helpers/utility';
import useError from 'lib/hooks/useError';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';

import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import Modal from 'components/shared/Modal/Modal';
import SegmentedControl from 'components/shared/SegmentedControl/SegmentedControl';
import Typography from 'components/shared/Typography/Typography';

import NewCard from '../../../../../../Patient/checkin/SecurePayment/components/PayWithCardModal/NewCard';
import SavedCards from '../../../../../../Patient/checkin/SecurePayment/components/PayWithCardModal/SavedCards';
import { cardOptions } from '../../../../../../Patient/checkin/SecurePayment/components/PayWithCardModal/lib/helpers';
import ErrorPaymentModal from '../../ErrorModal';

const SCROLL_THRESHOLD = 30;

const PayWithCardModal = ({ isOpen, handleClose, patientId }) => {
  const [active, setActive] = useState('credit-card');
  const { terminal } = usePaymentContext();
  const scrollRef = useRef(null);
  const { error, setErrorMessage } = useError();

  const {
    data: cards,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteFetchData({
    queryKey: 'cards',
    queryFn: (navigate, params) => getCards(navigate, params),
    formatData: (data) => iaRa(data).filter((card) => !!card?.id),
    params: { patientId, terminalId: terminal?.id, withCount: true },
    dependencies: [terminal?.id, patientId],
    options: {
      enabled: !!terminal?.id && !!patientId
    }
  });

  useEffect(() => {
    if (ia(cards)) {
      setActive('saved-cards');
    }
  }, [isLoading]);

  const closeModal = () => {
    handleClose();
    setActive('credit-card');
    setErrorMessage(null);
  };

  const handleScroll = useCallback(() => {
    const div = scrollRef.current;
    if (!div) return;

    const isNearBottom = div.scrollTop + div.clientHeight >= div.scrollHeight - SCROLL_THRESHOLD;

    if (isNearBottom && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const div = scrollRef.current;
    if (!div) return;

    const debounced = debounce(handleScroll, 200);
    div.addEventListener('scroll', debounced);

    return () => {
      div.removeEventListener('scroll', debounced);
      debounced.cancel();
    };
  }, [handleScroll, active]);

  if (error)
    return (
      <ErrorPaymentModal
        message={error}
        onClearError={() => setErrorMessage(null)}
        onClose={closeModal}
      />
    );

  return (
    <Modal
      isOpen={isOpen}
      handleClose={closeModal}
      isCentered
      customTitle={
        <Typography variant="p" color="neutral-800" weight="medium" size="small">
          Pay online
        </Typography>
      }
      footer={null}
      isFooter={false}>
      <div className="flex flex-col gap-4">
        {ia(cards) && (
          <SegmentedControl
            active={active}
            items={cardOptions(cards)}
            onClick={(option) => setActive(option.value)}
          />
        )}
        {active === 'saved-cards' && (
          <SavedCards ref={scrollRef} cards={cards} isFetchingNextPage={isFetchingNextPage} />
        )}
        {active === 'credit-card' && (
          <NewCard key="pay-with-new-card" onError={(error) => setErrorMessage(error)} />
        )}
      </div>
    </Modal>
  );
};

export default PayWithCardModal;
